import { RoleEnum } from '@acme/api';
import { toCurrency, useCurrency, useUser } from '@acme/domains';
import {
  AgentContractMainInfo,
  AgentRequisites,
  ApplicationActivity,
  ApplicationBaseInfo,
  ApplicationCompletedBanner,
  ApplicationEpoch,
  ApplicationMainInfo,
  ApplicationParticipants,
  ApplicationTitle,
  Commission,
  ContractInfo,
} from '@acme/features';
import { Page, Separator } from '@acme/foundation';
import {
  $agentContract,
  $application,
  $contract,
  $fee,
  $isLoading,
  $participants,
  applicationOpened,
  applicationReset,
} from '@entities/application';
import { Actions } from '@widgets/applicationActions';
import { $statusAlert } from '@widgets/applicationActions/model';
import { useUnit } from 'effector-react';
import { Alert, Card } from 'flowbite-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApplicationBreadcrumbs } from '../lib/breadcrumbs';
import { DocumentsCard } from './documentsCard';
import { LiquidityProvidersCard } from './liquidityProvidersCard';

export const ApplicationClientDetails = () => {
  const navigate = useNavigate();
  useCurrency();

  const {
    application,
    contract,
    fee,
    isLoading,
    statusAlert,
    agentContract,
    participants,
  } = useUnit({
    application: $application,
    contract: $contract,
    fee: $fee,
    isLoading: $isLoading,
    statusAlert: $statusAlert,
    onOpened: applicationOpened,
    onReset: applicationReset,
    agentContract: $agentContract,
    participants: $participants,
  });

  const breadcrumbItems = useMemo(
    () => getApplicationBreadcrumbs(navigate, application?.code),
    [navigate, application?.code]
  );

  const { user, organization } = useUser();
  return (
    <Page breadcrumbItems={breadcrumbItems} isLoading={isLoading}>
      {!isLoading && application && (
        <>
          {statusAlert && (
            <Alert color={statusAlert.color} className="mb-4">
              <span className="font-semibold">{statusAlert.title}</span>
              <p className="mt-1 text-sm">
                Причина: <em>{statusAlert.reason}</em>
              </p>
            </Alert>
          )}

          {application.epoch?.stepCode === 'TRANSFER-FINISHED' && (
            <Card>
              <div className="pb-6">
                <ApplicationCompletedBanner
                  currencySymbol={toCurrency(contract?.currency)?.symbol}
                  amount={contract?.amount}
                  completedAt={application.updatedAt}
                  onGoToDocuments={() => {
                    document
                      .getElementById('documents')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                />
              </div>
            </Card>
          )}
          <div className="text-gray-400 text-xs">
            Текущий шаг: {application.epoch?.stepCode ?? ''}
          </div>
          <Card className="divide-y divide-gray-200">
            <ApplicationTitle
              title={`${application.code} - ${application.name}`}
              currency={toCurrency(contract?.currency)}
              amount={contract?.amount}
            />
            <ApplicationBaseInfo
              application={application}
              contract={contract}
            />
            <Separator className="-mx-6" />
            <div className="flex justify-between items-center">
              <ApplicationEpoch epoch={application.epoch} />
              <Actions />
            </div>
          </Card>
          <Card>
            {organization?.role?.id !== RoleEnum.Client && (
              <ApplicationParticipants
                participants={participants}
                applicationId={application?.id ?? undefined}
              />
            )}

            <ApplicationMainInfo
              application={application}
              contract={contract}
            />
            {!!fee?.fee && (
              <Commission
                fee={fee}
                separateCommissions={
                  organization?.role?.id === RoleEnum.Admin &&
                  !contract?.isCryptoPayment
                }
              />
            )}
            <LiquidityProvidersCard />

            {agentContract && (
              <>
                <AgentRequisites agentContract={agentContract} />
                <AgentContractMainInfo agentContract={agentContract} />
              </>
            )}
            {contract?.uniqueContractNumber && (
              <ContractInfo contract={contract} />
            )}
          </Card>
          <div id="documents">
            <DocumentsCard />
          </div>
          {application.epoch?.stepCode !== 'TRANSFER-DRAFT' && (
            <ApplicationActivity />
          )}
        </>
      )}
    </Page>
  );
};
